import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatEUR, hasPerm, isOwner } from "@/lib/api";
import { useStore, withActiveStore } from "@/lib/storeContext";
import { useFeatures } from "@/lib/features";
import { isLodgingOnly } from "@/lib/activityCatalog";
import { isVeltaApk } from "@/lib/staffAccess";
import { TrendingUp, Receipt, Clock, ShoppingBag, ArrowRight, ScanLine, Package, FileBarChart, Star, Search, BedDouble, Users } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const Stat = ({ label, value, sub, icon: Icon, testid, to }) => {
  const body = (
    <>
      <div className="flex items-start justify-between mb-3">
        <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-[#71717A]">{label}</div>
        <Icon className="w-5 h-5 text-[#A1A1AA]" strokeWidth={1.5} />
      </div>
      <div className="font-mono text-3xl lg:text-[2.15rem] font-bold tracking-tighter text-[#18181B]">{value}</div>
      {sub && <div className="mt-2 text-xs text-[#71717A]">{sub}</div>}
    </>
  );
  if (to) {
    return (
      <Link to={to} data-testid={testid} className="bg-white border border-[#E4E4E7] rounded-2xl p-6 block transition-colors hover:border-[#18181B]">
        {body}
      </Link>
    );
  }
  return <div data-testid={testid} className="bg-white border border-[#E4E4E7] rounded-2xl p-6">{body}</div>;
};

const Quick = ({ to, icon: Icon, label, testid, primary }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`flex items-center gap-3 rounded-2xl px-5 h-16 text-sm font-medium btn-tactile ${
      primary
        ? "bg-[#B73C27] hover:bg-[#98301F] text-white"
        : "bg-white border border-[#E4E4E7] text-[#18181B] hover:border-[#18181B]"
    }`}
    style={primary ? { boxShadow: "0 8px 18px rgba(183,60,39,0.22)" } : undefined}
  >
    <Icon className="w-5 h-5 shrink-0" strokeWidth={1.8} />
    {label}
    {primary && <ArrowRight className="w-4 h-4 ml-auto" />}
  </Link>
);

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [tenant, setTenant] = useState(null);
  const { storeId } = useStore();
  const { hasFeature, modes } = useFeatures();
  const onlyLodging = isLodgingOnly(modes);
  const dedicatedLodging = onlyLodging && (modes || []).includes("alloggio_dedicato") && !(modes || []).includes("bnb");
  const [lodging, setLodging] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const r = await api.get("/stats/overview", { params: withActiveStore() });
        setStats(r.data && !Array.isArray(r.data) ? r.data : null);
      } catch {
        setStats(null);
      }
    };

    const fetchTenant = async () => {
      try {
        const r = await api.get("/tenant/me");
        setTenant(r.data && !Array.isArray(r.data) ? r.data : null);
      } catch {
        setTenant(null);
      }
    };

    fetchStats();
    fetchTenant();
  }, [storeId]);

  useEffect(() => {
    if (!onlyLodging) return;
    api.get("/hotel/stats").then((r) => setLodging(r.data)).catch(() => setLodging(null));
  }, [onlyLodging]);

  const statsIsValid = stats && !Array.isArray(stats);
  const tenantValid = tenant && !Array.isArray(tenant);
  const modeLabels = (Array.isArray(tenant?.modes) ? tenant.modes : []).map((m) => String(m || "").replace(/_/g, " ")).join(" · ");

  return (
    <div className="p-8 lg:p-12 max-w-6xl">
      <div className="mb-8">
        <h1 className="font-heading text-3xl lg:text-4xl font-semibold tracking-tight text-[#18181B] mb-1">
          {tenantValid && tenant?.business_name || "Benvenuto"}
        </h1>
        <p className="text-sm text-[#71717A]">
          {tenantValid && (modeLabels || (tenant?.mode ? tenant.mode.replace(/_/g, " ") : ""))}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-10">
        {hasFeature("pos") && hasPerm("cassa") && !dedicatedLodging && (isOwner() || isVeltaApk()) && (
          <Quick to="/cassa" icon={ScanLine} label="Apri Cassa" testid="overview-open-pos-btn" primary />
        )}
        {hasPerm("gestionale") && !onlyLodging && (
          <Quick to="/dashboard/articoli" icon={Package} label="Articoli" testid="overview-quick-products" />
        )}
        {!onlyLodging && <Quick to="/dashboard/conti-aperti" icon={Search} label="Conti aperti" testid="overview-quick-open-orders" />}
        {onlyLodging && !dedicatedLodging && <Quick to="/dashboard/alberghiero" icon={BedDouble} label="Alloggi" testid="overview-quick-hotel" />}
        {onlyLodging && hasPerm("gestionale") && (
          <Quick to="/dashboard/alberghiero?tab=guests" icon={Users} label="Clienti" testid="overview-quick-hotel-clients" />
        )}
        {hasPerm("statistiche") && (
          <Quick to="/dashboard/chiusure" icon={FileBarChart} label="Chiusure" testid="overview-quick-closures" />
        )}
        {hasPerm("gestionale") && !onlyLodging && (
          <Quick to="/dashboard/fedelta" icon={Star} label="Clienti" testid="overview-quick-loyalty" />
        )}
      </div>

      {onlyLodging && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Stat testid="overview-arrivals" label="Arrivi oggi" value={(dedicatedLodging ? lodging?.direct_arrivals_today : lodging?.arrivals_today) ?? "—"} sub={dedicatedLodging ? "clienti in arrivo" : "check-in"} icon={BedDouble} to="/dashboard/alberghiero?tab=guests" />
          {!dedicatedLodging && <Stat testid="overview-occupied" label="Camere occupate" value={lodging?.occupied ?? "—"} sub={`${lodging?.rooms ?? 0} camere`} icon={BedDouble} />}
          <Stat testid="overview-nights" label="Notti del mese" value={(dedicatedLodging ? lodging?.direct_nights_month : lodging?.nights_month) ?? "—"} sub={dedicatedLodging ? "giorni di permanenza" : "soggiorni"} icon={TrendingUp} />
          <Stat testid="overview-docs" label="Documenti" value={lodging?.documents ?? "—"} sub="pronti per Alloggiati e ROSS" icon={FileBarChart} to="/dashboard/alberghiero?tab=guests" />
        </div>
      )}

      {statsIsValid && !onlyLodging && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <Stat testid="overview-today-total" label="Incasso oggi" value={formatEUR(stats.today_total)} sub={`${stats.today_count} scontrini`} icon={TrendingUp} />
            <Stat testid="overview-month-total" label="Incasso mese" value={formatEUR(stats.month_total)} sub={`${stats.month_count} scontrini`} icon={Receipt} />
            <Stat testid="overview-avg-ticket" label="Scontrino medio" value={formatEUR(stats.avg_ticket)} sub="oggi" icon={ShoppingBag} />
            <Stat testid="overview-open-orders" label="Conti aperti" value={stats.open_orders} sub="Apri lista" icon={Clock} to="/dashboard/conti-aperti" />
          </div>

          <div className="bg-white border border-[#E4E4E7] rounded-2xl p-6">
            <h3 className="font-heading text-lg font-semibold text-[#18181B] mb-6">Ultimi 7 giorni</h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={Array.isArray(stats.last_7_days) ? stats.last_7_days : []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(d) => d?.slice(5) || ""} stroke="#A1A1AA" fontSize={11} fontFamily="IBM Plex Mono" />
                  <YAxis stroke="#A1A1AA" fontSize={11} fontFamily="IBM Plex Mono" tickFormatter={(v) => `€${v}`} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E4E4E7", fontFamily: "IBM Plex Sans", fontSize: 13 }} formatter={(v) => formatEUR(v)} />
                  <Line type="monotone" dataKey="total" stroke="#B73C27" strokeWidth={2.5} dot={{ r: 4, fill: "#B73C27" }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}